<template>
  <span>
    <!-- Boton de la cabecera: abre el reporte X del dia. -->
    <button
      type="button"
      class="btn btn-sm pos-report-btn pos-tap"
      :disabled="!store.branchId"
      @click="openModal"
    >
      <i class="fa-solid fa-chart-column" aria-hidden="true"></i>
      <span>{{ $t('pos.lbl_daily_report') }}</span>
    </button>

    <PosModal
      v-model="show"
      :title="$t('pos.lbl_daily_report')"
      :done-label="$t('pos.lbl_done')"
      :close-label="$t('pos.lbl_close')"
    >
      <AlertMessage class="mb-3" :message="error" />

      <!-- Selector de fecha (default hoy) -->
      <div class="d-flex align-items-end gap-2 mb-3">
        <div class="flex-grow-1">
          <label class="form-label small mb-1" for="pos-report-date">{{ $t('pos.lbl_date') }}</label>
          <input id="pos-report-date" type="date" class="form-control form-control-sm" v-model="date" @change="load" />
        </div>
        <button type="button" class="btn btn-sm btn-outline-secondary pos-tap" :disabled="busy" @click="load">
          <span v-if="busy" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <i v-else class="fa-solid fa-rotate" aria-hidden="true"></i>
        </button>
      </div>

      <template v-if="report">
        <!-- Cabecera: ventas + bruto -->
        <div class="pos-report-hero mb-3">
          <div>
            <span class="pos-report-hero-label">{{ $t('pos.lbl_sales_count') }}</span>
            <strong class="pos-report-hero-value">{{ report.sales_count }}</strong>
          </div>
          <div class="text-end">
            <span class="pos-report-hero-label">{{ $t('pos.lbl_gross_total') }}</span>
            <strong class="pos-report-hero-value text-primary">{{ money(report.gross_total) }}</strong>
          </div>
        </div>

        <!-- Por metodo de pago -->
        <h6 class="mb-2">{{ $t('pos.lbl_by_method') }}</h6>
        <div class="pos-cash-summary mb-3" v-if="report.by_method.length">
          <div class="d-flex justify-content-between" v-for="row in report.by_method" :key="row.method">
            <span class="text-muted text-capitalize">{{ methodLabel(row.method) }} <small>×{{ row.count }}</small></span>
            <strong>{{ money(row.total) }}</strong>
          </div>
        </div>
        <p class="text-muted small" v-else>{{ $t('pos.lbl_no_sales') }}</p>

        <!-- Desglose de ingresos -->
        <h6 class="mb-2">{{ $t('pos.lbl_breakdown') }}</h6>
        <div class="pos-cash-summary mb-3">
          <div class="d-flex justify-content-between"><span class="text-muted">{{ $t('pos.lbl_products_revenue') }}</span><span>{{ money(report.products_revenue) }}</span></div>
          <div class="d-flex justify-content-between"><span class="text-muted">{{ $t('pos.lbl_services_revenue') }}</span><span>{{ money(report.services_revenue) }}</span></div>
          <div class="d-flex justify-content-between" v-if="report.packages_revenue > 0"><span class="text-muted">{{ $t('pos.lbl_packages_revenue') }}</span><span>{{ money(report.packages_revenue) }}</span></div>
          <div class="d-flex justify-content-between"><span class="text-muted">− {{ $t('pos.lbl_discounts') }}</span><span>{{ money(report.discount_total) }}</span></div>
          <div class="d-flex justify-content-between"><span class="text-muted">+ {{ $t('pos.lbl_tax') }}</span><span>{{ money(report.tax_total) }}</span></div>
          <div class="d-flex justify-content-between"><span class="text-muted">+ {{ $t('pos.lbl_tips') }}</span><span>{{ money(report.tip_total) }}</span></div>
        </div>

        <!-- Arqueo en vivo de la caja -->
        <template v-if="report.cash_register">
          <h6 class="mb-2">{{ $t('pos.lbl_cash') }}</h6>
          <div class="pos-cash-summary">
            <div class="d-flex justify-content-between"><span class="text-muted">{{ $t('pos.lbl_opening_amount') }}</span><span>{{ money(report.cash_register.opening_amount) }}</span></div>
            <div class="d-flex justify-content-between"><span class="text-muted">+ {{ $t('pos.lbl_cash_sales') }}</span><span>{{ money(report.cash_register.cash_sales) }}</span></div>
            <div class="d-flex justify-content-between"><span class="text-muted">+ {{ $t('pos.lbl_cash_in') }}</span><span>{{ money(report.cash_register.income) }}</span></div>
            <div class="d-flex justify-content-between"><span class="text-muted">− {{ $t('pos.lbl_cash_out') }}</span><span>{{ money(report.cash_register.expense) }}</span></div>
            <div class="d-flex justify-content-between pt-1 border-top"><strong>{{ $t('pos.lbl_expected_amount') }}</strong><strong class="text-primary">{{ money(report.cash_register.expected_amount) }}</strong></div>
          </div>
        </template>
      </template>
    </PosModal>
  </span>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AlertMessage from './AlertMessage.vue'
import PosModal from './PosModal.vue'
import { usePos } from '../../store/pos'
import { posRequest } from '../usePosRequest'
import { POS_DAILY_REPORT, PAYMENT_METHODS } from '../constants/pos'
import { money } from '../money'

const store = usePos()
const { t } = useI18n()

const show = ref(false)
const busy = ref(false)
const error = ref('')
const report = ref(null)

// Fecha local en formato YYYY-MM-DD (no UTC, para no saltar de dia en la noche).
const todayLocal = () => {
  const d = new Date()
  const off = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - off).toISOString().slice(0, 10)
}
const date = ref(todayLocal())

const methodLabel = (method) => {
  const found = PAYMENT_METHODS.find((m) => m.value === method)
  return found ? t(found.label) : method
}

const openModal = async () => {
  error.value = ''
  show.value = true
  await load()
}

const load = async () => {
  if (!store.branchId) return

  busy.value = true
  error.value = ''

  const res = await posRequest(POS_DAILY_REPORT({ branch_id: store.branchId, date: date.value }))

  if (res.ok && res.data) {
    report.value = res.data
  } else {
    error.value = res.message || t('pos.msg_generic_error')
  }

  busy.value = false
}
</script>
